import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Milestone, CheckCircle, Clock, AlertTriangle, Target,
  FileText, FolderTree, Archive, Send, Plus, Edit, Trash2,
  RefreshCw, Calendar, Users, Building2, Globe, ChevronRight,
  BarChart3, PieChart, TrendingUp, Loader2, Play, Pause, X,
  ChevronDown, ChevronUp, Flag, Link2, Settings, Download
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Milestone status colors
const MILESTONE_STATUS_COLORS = {
  planned: 'bg-neutral-100 text-neutral-700 border-neutral-300',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
  completed: 'bg-green-100 text-green-700 border-green-300',
  delayed: 'bg-amber-100 text-amber-700 border-amber-300',
  cancelled: 'bg-red-100 text-red-700 border-red-300'
};

// Zone colors for TMF completeness
const ZONE_COLORS = [
  '#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED',
  '#0891B2', '#BE185D', '#4F46E5', '#16A34A', '#EA580C', '#8B5CF6'
];

export default function ETMFManagementPage() {
  const { user, getAuthHeaders, isAdmin, selectedStudy } = useAuth();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('milestones');
  const [loading, setLoading] = useState(true);

  // Milestones state
  const [milestones, setMilestones] = useState([]);
  const [milestoneTypes, setMilestoneTypes] = useState([]);
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneForm, setMilestoneForm] = useState({
    name: '',
    milestone_type: '',
    description: '',
    planned_date: '',
    status: 'planned',
    owner: ''
  });

  // TMF Completeness state
  const [completeness, setCompleteness] = useState(null);
  const [siteCompleteness, setSiteCompleteness] = useState([]);
  const [countryCompleteness, setCountryCompleteness] = useState([]);

  // EDL Templates state
  const [edlTemplates, setEdlTemplates] = useState([]);
  const [showEDLDialog, setShowEDLDialog] = useState(false);
  const [edlForm, setEdlForm] = useState({
    name: '',
    description: '',
    study_phase: 'Phase III',
    is_default: false,
    items: []
  });

  // TMF Index state
  const [tmfIndex, setTmfIndex] = useState([]);
  const [showIndexDialog, setShowIndexDialog] = useState(false);

  // Archival state
  const [archiveStatus, setArchiveStatus] = useState(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiveForm, setArchiveForm] = useState({
    archive_reason: '',
    export_format: 'pdf',
    include_audit_trail: true
  });

  // Transfer state
  const [transfers, setTransfers] = useState([]);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [studies, setStudies] = useState([]);
  const [transferForm, setTransferForm] = useState({
    target_study_id: '',
    package_type: 'full',
    transfer_reason: ''
  });

  const studyId = selectedStudy?.study_id;

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!studyId) return;
    setLoading(true);
    
    try {
      const { headers } = getAuthHeaders();
      
      // Fetch milestones
      const msRes = await axios.get(`${API_URL}/api/etmf/studies/${studyId}/milestones`, { headers });
      setMilestones(msRes.data?.milestones || []);
      
      // Fetch milestone types
      const typesRes = await axios.get(`${API_URL}/api/etmf/milestone-types`, { headers });
      setMilestoneTypes(typesRes.data?.milestone_types || []);
      
      // Fetch TMF completeness
      try {
        const compRes = await axios.get(`${API_URL}/api/etmf/studies/${studyId}/completeness`, { headers });
        setCompleteness(compRes.data);
        
        const siteRes = await axios.get(`${API_URL}/api/etmf/studies/${studyId}/completeness/by-site`, { headers });
        setSiteCompleteness(siteRes.data?.sites || []);
        
        const countryRes = await axios.get(`${API_URL}/api/etmf/studies/${studyId}/completeness/by-country`, { headers });
        setCountryCompleteness(countryRes.data?.countries || []);
      } catch (e) {
        console.log('Completeness data not available');
      }
      
      // Fetch EDL templates
      const edlRes = await axios.get(`${API_URL}/api/etmf/edl-templates`, { headers });
      setEdlTemplates(edlRes.data?.templates || []);
      
      // Fetch TMF Index
      const indexRes = await axios.get(`${API_URL}/api/etmf/tmf-index`, { headers });
      setTmfIndex(indexRes.data?.tmf_index || []);
      
      // Fetch archive status
      try {
        const archRes = await axios.get(`${API_URL}/api/etmf/studies/${studyId}/archive-status`, { headers });
        setArchiveStatus(archRes.data);
      } catch (e) {
        setArchiveStatus({ status: 'not_archived' });
      }
      
      // Fetch transfers
      const transRes = await axios.get(`${API_URL}/api/etmf/transfers?study_id=${studyId}`, { headers });
      setTransfers(transRes.data?.transfers || []);
      
      // Fetch all studies for transfer target selection
      const studiesRes = await axios.get(`${API_URL}/api/studies`, { headers });
      setStudies(studiesRes.data || []);
      
    } catch (error) {
      console.error('Error fetching eTMF data:', error);
      toast.error('Failed to load eTMF data');
    } finally {
      setLoading(false);
    }
  }, [studyId, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Milestone CRUD operations
  const handleCreateMilestone = async () => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/etmf/milestones`, {
        study_id: studyId,
        ...milestoneForm
      }, { headers });
      
      toast.success('Milestone created');
      setShowMilestoneDialog(false);
      setMilestoneForm({ name: '', milestone_type: '', description: '', planned_date: '', status: 'planned', owner: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create milestone');
    }
  };

  const handleUpdateMilestone = async () => {
    if (!editingMilestone) return;
    try {
      const { headers } = getAuthHeaders();
      await axios.put(`${API_URL}/api/etmf/milestones/${editingMilestone.id}`, milestoneForm, { headers });
      
      toast.success('Milestone updated');
      setShowMilestoneDialog(false);
      setEditingMilestone(null);
      setMilestoneForm({ name: '', milestone_type: '', description: '', planned_date: '', status: 'planned', owner: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to update milestone');
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if (!confirm('Are you sure you want to delete this milestone?')) return;
    try {
      const { headers } = getAuthHeaders();
      await axios.delete(`${API_URL}/api/etmf/milestones/${milestoneId}`, { headers });
      toast.success('Milestone deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete milestone');
    }
  };

  const handleCreateBulkMilestones = async () => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/etmf/studies/${studyId}/milestones/bulk`, {}, { headers });
      toast.success('Standard milestones created');
      fetchData();
    } catch (error) {
      toast.error('Failed to create milestones');
    }
  };

  // EDL operations
  const handleCreateEDLTemplate = async () => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/etmf/edl-templates`, edlForm, { headers });
      toast.success('EDL template created');
      setShowEDLDialog(false);
      setEdlForm({ name: '', description: '', study_phase: 'Phase III', is_default: false, items: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to create EDL template');
    }
  };

  // Archive operations
  const handleArchiveStudy = async () => {
    try {
      const { headers } = getAuthHeaders();
      const res = await axios.post(`${API_URL}/api/etmf/studies/${studyId}/archive`, {
        study_id: studyId,
        ...archiveForm
      }, { headers });
      
      if (res.data.status === 'warning') {
        toast.warning(res.data.message);
      } else {
        toast.success('Study archived successfully');
      }
      setShowArchiveDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to archive study');
    }
  };

  // Transfer operations
  const handleCreateTransfer = async () => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/etmf/transfers`, {
        source_study_id: studyId,
        ...transferForm
      }, { headers });
      toast.success('Transfer package created');
      setShowTransferDialog(false);
      setTransferForm({ target_study_id: '', package_type: 'full', transfer_reason: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create transfer');
    }
  };

  const handleExecuteTransfer = async (transferId) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/etmf/transfers/${transferId}/execute`, {}, { headers });
      toast.success('Transfer executed successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to execute transfer');
    }
  };

  // Initialize TMF Index
  const handleInitializeTMFIndex = async () => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/etmf/tmf-index/initialize-default`, {}, { headers });
      toast.success('TMF Index initialized');
      fetchData();
    } catch (error) {
      toast.error('Failed to initialize TMF Index');
    }
  };

  if (!studyId) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">No Study Selected</h2>
          <p className="text-neutral-500 mb-4">Please select a study to access eTMF features.</p>
          <Button onClick={() => navigate('/studies')} className="bg-[#1E3A5F] hover:bg-[#152B47]">
            Select Study
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <header className="border-b border-neutral-200 bg-[#1E3A5F] shadow-sm">
        <div className="w-full max-w-full px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard?view=all')}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-5 w-px bg-white/20"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#2563EB] flex items-center justify-center">
                  <FolderTree className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white">eTMF Management</h1>
                  <p className="text-[10px] text-white/60">{selectedStudy?.study_name}</p>
                </div>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full max-w-full px-4 sm:px-6 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-neutral-200 p-1 mb-4">
            <TabsTrigger value="milestones" className="data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white">
              <Milestone className="w-4 h-4 mr-2" />
              Milestones
            </TabsTrigger>
            <TabsTrigger value="completeness" className="data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white">
              <PieChart className="w-4 h-4 mr-2" />
              TMF Completeness
            </TabsTrigger>
            <TabsTrigger value="edl" className="data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              EDL Templates
            </TabsTrigger>
            <TabsTrigger value="index" className="data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-2" />
              TMF Index
            </TabsTrigger>
            <TabsTrigger value="archive" className="data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white">
              <Archive className="w-4 h-4 mr-2" />
              Archival
            </TabsTrigger>
            <TabsTrigger value="transfer" className="data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white">
              <Send className="w-4 h-4 mr-2" />
              TMF Transfer
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
            </div>
          ) : (
            <>
              {/* Milestones Tab */}
              <TabsContent value="milestones" className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">Study Milestones</h2>
                    <p className="text-sm text-neutral-500">Track key study events and their dependencies</p>
                  </div>
                  <div className="flex gap-2">
                    {milestones.length === 0 && (
                      <Button onClick={handleCreateBulkMilestones} variant="outline" className="border-[#2563EB] text-[#2563EB]">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Standard Milestones
                      </Button>
                    )}
                    <Button onClick={() => { setEditingMilestone(null); setShowMilestoneDialog(true); }} className="bg-[#1E3A5F] hover:bg-[#152B47]">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Milestone
                    </Button>
                  </div>
                </div>

                {/* Milestone Timeline */}
                <div className="space-y-3">
                  {milestones.length === 0 ? (
                    <Card className="p-8 text-center bg-white border-neutral-200">
                      <Milestone className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-neutral-900 mb-2">No Milestones</h3>
                      <p className="text-neutral-500 mb-4">Create standard milestones to track study progress</p>
                    </Card>
                  ) : (
                    milestones.sort((a, b) => (a.order || 0) - (b.order || 0)).map((milestone, idx) => (
                      <Card key={milestone.id} className="bg-white border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Timeline indicator */}
                            <div className="flex flex-col items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                milestone.status === 'completed' ? 'bg-green-500' :
                                milestone.status === 'in_progress' ? 'bg-blue-500' :
                                milestone.status === 'delayed' ? 'bg-amber-500' : 'bg-neutral-300'
                              }`}>
                                {milestone.status === 'completed' ? (
                                  <CheckCircle className="w-4 h-4 text-white" />
                                ) : milestone.status === 'in_progress' ? (
                                  <Play className="w-4 h-4 text-white" />
                                ) : (
                                  <Clock className="w-4 h-4 text-white" />
                                )}
                              </div>
                              {idx < milestones.length - 1 && (
                                <div className={`w-0.5 h-12 ${
                                  milestone.status === 'completed' ? 'bg-green-300' : 'bg-neutral-200'
                                }`} />
                              )}
                            </div>

                            {/* Milestone content */}
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h3 className="font-semibold text-neutral-900">{milestone.name}</h3>
                                  <p className="text-sm text-neutral-500">{milestone.description || milestone.milestone_type}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className={MILESTONE_STATUS_COLORS[milestone.status] || MILESTONE_STATUS_COLORS.planned}>
                                    {milestone.status?.replace('_', ' ')}
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingMilestone(milestone);
                                      setMilestoneForm({
                                        name: milestone.name,
                                        milestone_type: milestone.milestone_type,
                                        description: milestone.description || '',
                                        planned_date: milestone.planned_date || '',
                                        status: milestone.status,
                                        owner: milestone.owner || ''
                                      });
                                      setShowMilestoneDialog(true);
                                    }}
                                    className="text-neutral-500 hover:text-[#2563EB]"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteMilestone(milestone.id)}
                                    className="text-neutral-500 hover:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>

                              <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500">
                                {milestone.planned_date && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Planned: {milestone.planned_date}
                                  </span>
                                )}
                                {milestone.actual_date && (
                                  <span className="flex items-center gap-1 text-green-600">
                                    <CheckCircle className="w-3 h-3" />
                                    Completed: {milestone.actual_date}
                                  </span>
                                )}
                                {milestone.dependencies?.length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Link2 className="w-3 h-3" />
                                    {milestone.dependencies.length} dependency(ies)
                                  </span>
                                )}
                                {!milestone.dependencies_met && (
                                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                                    Waiting on dependencies
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* TMF Completeness Tab */}
              <TabsContent value="completeness" className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">TMF Completeness Dashboard</h2>
                    <p className="text-sm text-neutral-500">Track document completeness by Zone, Section, and Artifact</p>
                  </div>
                </div>

                {completeness ? (
                  <>
                    {/* Overall Completeness */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card className="bg-white border-neutral-200 shadow-sm col-span-1">
                        <CardContent className="p-4 text-center">
                          <div className="relative w-24 h-24 mx-auto">
                            <svg className="w-24 h-24 transform -rotate-90">
                              <circle cx="48" cy="48" r="40" stroke="#E2E8F0" strokeWidth="8" fill="none" />
                              <circle cx="48" cy="48" r="40" stroke="#2563EB" strokeWidth="8" fill="none"
                                strokeDasharray={`${completeness.overall_percentage * 2.51} 251`}
                                strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-2xl font-bold text-neutral-900">{completeness.overall_percentage}%</span>
                            </div>
                          </div>
                          <p className="text-sm font-medium text-neutral-700 mt-2">Overall Completeness</p>
                          <p className="text-xs text-neutral-500">{completeness.total_actual} / {completeness.total_expected} docs</p>
                        </CardContent>
                      </Card>

                      <Card className="bg-white border-neutral-200 shadow-sm col-span-3">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Zone Completeness</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {Object.entries(completeness.zone_completeness || {}).map(([zoneNum, zone], idx) => (
                              <div key={zoneNum} className="flex items-center gap-3">
                                <div className="w-20 text-xs text-neutral-600 truncate" title={zone.name}>
                                  {zoneNum}. {zone.name}
                                </div>
                                <div className="flex-1">
                                  <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full rounded-full transition-all"
                                      style={{ 
                                        width: `${zone.percentage}%`,
                                        backgroundColor: ZONE_COLORS[idx % ZONE_COLORS.length]
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="w-12 text-xs text-right text-neutral-600">
                                  {zone.percentage}%
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Site and Country Completeness */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card className="bg-white border-neutral-200 shadow-sm">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#2563EB]" />
                            Site Completeness
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ScrollArea className="h-48">
                            {siteCompleteness.length === 0 ? (
                              <p className="text-sm text-neutral-500 text-center py-4">No site data available</p>
                            ) : (
                              <div className="space-y-3">
                                {siteCompleteness.map(site => (
                                  <div key={site.site_id} className="flex items-center justify-between p-2 bg-neutral-50 rounded">
                                    <div>
                                      <p className="font-medium text-neutral-900 text-sm">{site.site_code}</p>
                                      <p className="text-xs text-neutral-500">{site.site_name}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold text-neutral-900">{site.percentage}%</p>
                                      <p className="text-xs text-neutral-500">{site.actual}/{site.expected}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </ScrollArea>
                        </CardContent>
                      </Card>

                      <Card className="bg-white border-neutral-200 shadow-sm">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Globe className="w-4 h-4 text-[#2563EB]" />
                            Country Completeness
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ScrollArea className="h-48">
                            {countryCompleteness.length === 0 ? (
                              <p className="text-sm text-neutral-500 text-center py-4">No country data available</p>
                            ) : (
                              <div className="space-y-3">
                                {countryCompleteness.map(country => (
                                  <div key={country.country} className="flex items-center justify-between p-2 bg-neutral-50 rounded">
                                    <p className="font-medium text-neutral-900 text-sm">{country.country}</p>
                                    <div className="text-right">
                                      <p className="font-semibold text-neutral-900">{country.percentage}%</p>
                                      <p className="text-xs text-neutral-500">{country.actual}/{country.expected}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Missing Artifacts */}
                    {completeness.missing_artifacts?.length > 0 && (
                      <Card className="bg-white border-neutral-200 shadow-sm">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2 text-amber-600">
                            <AlertTriangle className="w-4 h-4" />
                            Missing Artifacts ({completeness.missing_artifacts.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ScrollArea className="h-48">
                            <div className="space-y-2">
                              {completeness.missing_artifacts.map((artifact, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 border border-amber-200 rounded">
                                  <div>
                                    <p className="font-medium text-neutral-900 text-sm">{artifact.artifact}</p>
                                    <p className="text-xs text-neutral-500">{artifact.zone} &gt; {artifact.section}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge className={artifact.required ? 'bg-red-100 text-red-700' : 'bg-neutral-100 text-neutral-600'}>
                                      {artifact.required ? 'Required' : 'Optional'}
                                    </Badge>
                                    <span className="text-sm text-neutral-600">{artifact.actual}/{artifact.expected}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    )}
                  </>
                ) : (
                  <Card className="p-8 text-center bg-white border-neutral-200">
                    <PieChart className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-neutral-900 mb-2">No Completeness Data</h3>
                    <p className="text-neutral-500">Upload documents to see TMF completeness metrics</p>
                  </Card>
                )}
              </TabsContent>

              {/* EDL Templates Tab */}
              <TabsContent value="edl" className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">Expected Document Lists (EDL)</h2>
                    <p className="text-sm text-neutral-500">Define expected documents per study phase</p>
                  </div>
                  <Button onClick={() => setShowEDLDialog(true)} className="bg-[#1E3A5F] hover:bg-[#152B47]">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {edlTemplates.length === 0 ? (
                    <Card className="col-span-full p-8 text-center bg-white border-neutral-200">
                      <FileText className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-neutral-900 mb-2">No EDL Templates</h3>
                      <p className="text-neutral-500">Create templates to define expected documents</p>
                    </Card>
                  ) : (
                    edlTemplates.map(template => (
                      <Card key={template.id} className="bg-white border-neutral-200 shadow-sm">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base">{template.name}</CardTitle>
                            {template.is_default && (
                              <Badge className="bg-[#2563EB]/10 text-[#2563EB]">Default</Badge>
                            )}
                          </div>
                          <CardDescription>{template.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-neutral-500">Phase:</span>
                              <span className="font-medium">{template.study_phase}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-neutral-500">Items:</span>
                              <span className="font-medium">{template.items?.length || 0}</span>
                            </div>
                          </div>
                          <div className="mt-4 flex gap-2">
                            <Button size="sm" variant="outline" className="flex-1">
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8]">
                              Apply to Study
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* TMF Index Tab */}
              <TabsContent value="index" className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">TMF Index Configuration</h2>
                    <p className="text-sm text-neutral-500">Configure TMF Reference Model structure</p>
                  </div>
                  <div className="flex gap-2">
                    {tmfIndex.length === 0 && (
                      <Button onClick={handleInitializeTMFIndex} variant="outline" className="border-[#2563EB] text-[#2563EB]">
                        <Plus className="w-4 h-4 mr-2" />
                        Initialize Default Index
                      </Button>
                    )}
                    <Button onClick={() => setShowIndexDialog(true)} className="bg-[#1E3A5F] hover:bg-[#152B47]">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Entry
                    </Button>
                  </div>
                </div>

                <Card className="bg-white border-neutral-200 shadow-sm">
                  <CardContent className="p-0">
                    <ScrollArea className="h-[500px]">
                      <table className="w-full">
                        <thead className="bg-neutral-50 sticky top-0">
                          <tr>
                            <th className="text-left p-3 text-xs font-semibold text-neutral-600">Zone</th>
                            <th className="text-left p-3 text-xs font-semibold text-neutral-600">Section</th>
                            <th className="text-left p-3 text-xs font-semibold text-neutral-600">Artifact</th>
                            <th className="text-left p-3 text-xs font-semibold text-neutral-600">Level</th>
                            <th className="text-left p-3 text-xs font-semibold text-neutral-600">Required</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tmfIndex.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="text-center py-8 text-neutral-500">
                                No TMF Index entries. Click "Initialize Default Index" to create.
                              </td>
                            </tr>
                          ) : (
                            tmfIndex.map((entry, idx) => (
                              <tr key={entry.id || idx} className="border-t border-neutral-100 hover:bg-neutral-50">
                                <td className="p-3">
                                  <div>
                                    <span className="font-medium text-neutral-900">{entry.zone_number}</span>
                                    <p className="text-xs text-neutral-500">{entry.zone_name}</p>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <div>
                                    <span className="font-medium text-neutral-900">{entry.section_number}</span>
                                    <p className="text-xs text-neutral-500">{entry.section_name}</p>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <div>
                                    <span className="font-medium text-neutral-900">{entry.artifact_number}</span>
                                    <p className="text-xs text-neutral-500">{entry.artifact_name}</p>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <Badge className={
                                    entry.level === 'study' ? 'bg-blue-100 text-blue-700' :
                                    entry.level === 'country' ? 'bg-purple-100 text-purple-700' :
                                    'bg-green-100 text-green-700'
                                  }>
                                    {entry.level}
                                  </Badge>
                                </td>
                                <td className="p-3">
                                  {entry.required ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <X className="w-4 h-4 text-neutral-400" />
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Archival Tab */}
              <TabsContent value="archive" className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">Study Archival</h2>
                    <p className="text-sm text-neutral-500">Archive completed studies for long-term retention</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="bg-white border-neutral-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-base">Archive Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {archiveStatus?.status === 'archived' ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <Archive className="w-8 h-8 text-green-600" />
                            <div>
                              <p className="font-semibold text-green-900">Study Archived</p>
                              <p className="text-sm text-green-700">Archived on {archiveStatus.archive_date}</p>
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-neutral-500">Reason:</span>
                              <span>{archiveStatus.archive_reason}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-neutral-500">Documents:</span>
                              <span>{archiveStatus.document_count}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-neutral-500">Completeness:</span>
                              <span>{archiveStatus.completeness_at_archive}%</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 p-4 bg-neutral-50 border border-neutral-200 rounded-lg">
                            <Archive className="w-8 h-8 text-neutral-400" />
                            <div>
                              <p className="font-semibold text-neutral-900">Not Archived</p>
                              <p className="text-sm text-neutral-500">This study is currently active</p>
                            </div>
                          </div>
                          <Button onClick={() => setShowArchiveDialog(true)} className="w-full bg-[#1E3A5F] hover:bg-[#152B47]">
                            <Archive className="w-4 h-4 mr-2" />
                            Archive Study
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-neutral-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-base">Archive Requirements</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded">
                          {completeness?.overall_percentage >= 90 ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                          )}
                          <div>
                            <p className="font-medium text-neutral-900">TMF Completeness ≥ 90%</p>
                            <p className="text-sm text-neutral-500">Current: {completeness?.overall_percentage || 0}%</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium text-neutral-900">All milestones completed</p>
                            <p className="text-sm text-neutral-500">
                              {milestones.filter(m => m.status === 'completed').length}/{milestones.length} complete
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium text-neutral-900">Audit trail exported</p>
                            <p className="text-sm text-neutral-500">Will be included in archive</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Transfer Tab */}
              <TabsContent value="transfer" className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">TMF Transfer</h2>
                    <p className="text-sm text-neutral-500">Transfer documents between studies or systems</p>
                  </div>
                  <Button onClick={() => setShowTransferDialog(true)} className="bg-[#1E3A5F] hover:bg-[#152B47]">
                    <Send className="w-4 h-4 mr-2" />
                    New Transfer
                  </Button>
                </div>

                <div className="space-y-3">
                  {transfers.length === 0 ? (
                    <Card className="p-8 text-center bg-white border-neutral-200">
                      <Send className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-neutral-900 mb-2">No Transfers</h3>
                      <p className="text-neutral-500">Create a transfer package to move documents</p>
                    </Card>
                  ) : (
                    transfers.map(transfer => (
                      <Card key={transfer.id} className="bg-white border-neutral-200 shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-neutral-900">
                                Transfer to {transfer.target_study_id ? 'Study' : 'External System'}
                              </h3>
                              <p className="text-sm text-neutral-500">
                                {transfer.document_count} documents • {transfer.package_type} package
                              </p>
                              <p className="text-xs text-neutral-400 mt-1">
                                Created: {format(new Date(transfer.created_at), 'MMM d, yyyy HH:mm')}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={
                                transfer.status === 'completed' ? 'bg-green-100 text-green-700' :
                                transfer.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                transfer.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-neutral-100 text-neutral-700'
                              }>
                                {transfer.status}
                              </Badge>
                              {transfer.status === 'pending' && (
                                <Button
                                  size="sm"
                                  onClick={() => handleExecuteTransfer(transfer.id)}
                                  className="bg-[#2563EB] hover:bg-[#1D4ED8]"
                                >
                                  <Play className="w-3 h-3 mr-1" />
                                  Execute
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>

      {/* Milestone Dialog */}
      <Dialog open={showMilestoneDialog} onOpenChange={setShowMilestoneDialog}>
        <DialogContent className="bg-white border-neutral-200 max-w-md">
          <DialogHeader>
            <DialogTitle>{editingMilestone ? 'Edit Milestone' : 'Create Milestone'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={milestoneForm.name}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                placeholder="Milestone name"
              />
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={milestoneForm.milestone_type} onValueChange={(v) => setMilestoneForm({ ...milestoneForm, milestone_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {milestoneTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={milestoneForm.status} onValueChange={(v) => setMilestoneForm({ ...milestoneForm, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="delayed">Delayed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Planned Date</Label>
              <Input
                type="date"
                value={milestoneForm.planned_date}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, planned_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={milestoneForm.description}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMilestoneDialog(false)}>Cancel</Button>
            <Button onClick={editingMilestone ? handleUpdateMilestone : handleCreateMilestone} className="bg-[#1E3A5F] hover:bg-[#152B47]">
              {editingMilestone ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent className="bg-white border-neutral-200 max-w-md">
          <DialogHeader>
            <DialogTitle>Archive Study</DialogTitle>
            <DialogDescription>This will lock the study and prepare it for long-term retention.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Archive Reason *</Label>
              <Textarea
                value={archiveForm.archive_reason}
                onChange={(e) => setArchiveForm({ ...archiveForm, archive_reason: e.target.value })}
                placeholder="e.g., Study completed, regulatory submission done"
              />
            </div>
            <div>
              <Label>Export Format</Label>
              <Select value={archiveForm.export_format} onValueChange={(v) => setArchiveForm({ ...archiveForm, export_format: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="native">Native Format</SelectItem>
                  <SelectItem value="both">Both PDF and Native</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>Cancel</Button>
            <Button onClick={handleArchiveStudy} className="bg-[#1E3A5F] hover:bg-[#152B47]">
              Archive Study
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="bg-white border-neutral-200 max-w-md">
          <DialogHeader>
            <DialogTitle>Create TMF Transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Target Study</Label>
              <Select value={transferForm.target_study_id} onValueChange={(v) => setTransferForm({ ...transferForm, target_study_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select target study" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">External System</SelectItem>
                  {studies.filter(s => s.id !== studyId).map(study => (
                    <SelectItem key={study.id} value={study.id}>{study.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Package Type</Label>
              <Select value={transferForm.package_type} onValueChange={(v) => setTransferForm({ ...transferForm, package_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full TMF</SelectItem>
                  <SelectItem value="country">Country-specific</SelectItem>
                  <SelectItem value="site">Site-specific</SelectItem>
                  <SelectItem value="custom">Custom Selection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Transfer Reason</Label>
              <Textarea
                value={transferForm.transfer_reason}
                onChange={(e) => setTransferForm({ ...transferForm, transfer_reason: e.target.value })}
                placeholder="Reason for transfer"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTransfer} className="bg-[#1E3A5F] hover:bg-[#152B47]">
              Create Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
